/* @jsx h */
import {
  computed,
  defineComponent,
  h,
  onBeforeUnmount,
  onMounted,
  readonly,
  ref,
  Teleport,
} from "vue";
import { instrumentation } from "@lemmonhq/analytics";
import { useLocalization } from "@lemmonhq/uikit";

// Module-level singleton so non-component code (e.g. routing-fetch.ts) can
// report network problems without needing Vue's inject/provide.
const isFetchFailing = ref(false);
const isBrowserOffline = ref(
  typeof navigator !== "undefined" ? !navigator.onLine : false,
);

export type NetworkFetchReportContext = {
  serverId?: string;
  targetHost?: string;
  errorType?: "http" | "network" | "timeout" | "unknown";
  statusCode?: number;
  requestMethod?: string;
};

const getTimestamp = (timestamp: number) => new Date(timestamp).toISOString();

let browserStatusChangedAt = Date.now();
let fetchDegradedAt: number | null = null;
let consecutiveFetchFailures = 0;
let firstFetchFailure: NetworkFetchReportContext | null = null;
let lastFetchFailure: NetworkFetchReportContext | null = null;

const getFetchContextProperties = (context: NetworkFetchReportContext) => ({
  ...(context.serverId ? { server_id: context.serverId } : {}),
  ...(context.targetHost ? { target_host: context.targetHost } : {}),
  ...(context.errorType ? { error_type: context.errorType } : {}),
  ...(context.statusCode !== undefined
    ? { status_code: context.statusCode }
    : {}),
  ...(context.requestMethod ? { request_method: context.requestMethod } : {}),
});

export const reportNetworkFetchError = (
  context: NetworkFetchReportContext = {},
) => {
  const timestamp = Date.now();
  consecutiveFetchFailures += 1;
  lastFetchFailure = context;

  if (isFetchFailing.value) return;

  isFetchFailing.value = true;
  fetchDegradedAt = timestamp;
  firstFetchFailure = context;
  instrumentation.track("network_fetch_degraded", {
    source: "routing_fetch",
    timestamp: getTimestamp(timestamp),
    navigator_online:
      typeof navigator !== "undefined" ? navigator.onLine : undefined,
    consecutive_failures: consecutiveFetchFailures,
    ...getFetchContextProperties(context),
  });
};

export const reportNetworkFetchSuccess = (
  context: NetworkFetchReportContext = {},
) => {
  if (!isFetchFailing.value) return;

  const timestamp = Date.now();
  instrumentation.track("network_fetch_recovered", {
    source: "routing_fetch",
    timestamp: getTimestamp(timestamp),
    degraded_duration_ms:
      fetchDegradedAt === null ? 0 : timestamp - fetchDegradedAt,
    consecutive_failures: consecutiveFetchFailures,
    navigator_online:
      typeof navigator !== "undefined" ? navigator.onLine : undefined,
    ...getFetchContextProperties(context),
    ...(firstFetchFailure?.serverId
      ? { first_failed_server_id: firstFetchFailure.serverId }
      : {}),
    ...(firstFetchFailure?.targetHost
      ? { first_failed_target_host: firstFetchFailure.targetHost }
      : {}),
    ...(lastFetchFailure?.serverId
      ? { last_failed_server_id: lastFetchFailure.serverId }
      : {}),
    ...(lastFetchFailure?.targetHost
      ? { last_failed_target_host: lastFetchFailure.targetHost }
      : {}),
    ...(lastFetchFailure?.errorType
      ? { last_error_type: lastFetchFailure.errorType }
      : {}),
  });

  isFetchFailing.value = false;
  fetchDegradedAt = null;
  consecutiveFetchFailures = 0;
  firstFetchFailure = null;
  lastFetchFailure = null;
};

/**
 * Read-only view onto the module-level singletons above. Works outside
 * `NetworkStatusProvider`'s subtree too, since the refs aren't scoped to
 * `provide`/`inject` — but the provider must still be mounted once, at the
 * app root, for the `online`/`offline` listeners to be attached at all.
 */
export const useNetworkStatus = () => ({
  isBrowserOffline: readonly(isBrowserOffline),
  isFetchFailing: readonly(isFetchFailing),
  isDegraded: computed(() => isBrowserOffline.value || isFetchFailing.value),
});

export const NetworkStatusProvider = defineComponent({
  name: "NetworkStatusProvider",
  setup(_, { slots }) {
    const { t } = useLocalization();
    const trackBrowserStatus = (status: "online" | "offline") => {
      const wasOffline = isBrowserOffline.value;
      const isOffline = status === "offline";
      if (wasOffline === isOffline) return;

      const timestamp = Date.now();
      const previousStateDuration = timestamp - browserStatusChangedAt;
      isBrowserOffline.value = isOffline;
      browserStatusChangedAt = timestamp;

      instrumentation.track(`network_status_${status}`, {
        status,
        source: "browser_event",
        timestamp: getTimestamp(timestamp),
        previous_status: wasOffline ? "offline" : "online",
        previous_state_duration_ms: previousStateDuration,
        ...(status === "online"
          ? { offline_duration_ms: previousStateDuration }
          : { online_duration_ms: previousStateDuration }),
        navigator_online:
          typeof navigator !== "undefined" ? navigator.onLine : undefined,
      });
    };

    const handleOffline = () => {
      trackBrowserStatus("offline");
    };
    const handleOnline = () => {
      trackBrowserStatus("online");
    };

    onMounted(() => {
      window.addEventListener("offline", handleOffline);
      window.addEventListener("online", handleOnline);
    });

    onBeforeUnmount(() => {
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
    });

    // Banner disabled: keep network tracking/instrumentation but never show the
    // "Your network is having problems" overlay.
    const showBanner = computed(() => false);

    return () => (
      <>
        {slots.default?.()}
        <Teleport to="body">
          {showBanner.value ? (
            <div
              class="fixed left-1/2 z-[10000000] -translate-x-1/2 pointer-events-none"
              style={{ top: "50px" }}
            >
              <div class="alert alert-warning shadow-xl py-2 px-4 min-h-0">
                <div class="flex items-center justify-center min-w-[270px] gap-2.5">
                  <span class="material-symbols-outlined text-lg">
                    wifi_off
                  </span>
                  <span class="text-sm font-medium">
                    {t("general.errors.network_having_problems")}
                  </span>
                </div>
              </div>
            </div>
          ) : null}
        </Teleport>
      </>
    );
  },
});
